/**
 * src/lib/visit.js — tell the server somebody arrived.
 *
 * ⚠️ A BEACON, NOT AN API CALL. It is deliberately NOT routed through
 * lib/api.js: that wrapper reports every non-2xx into `platform_error` so a
 * failing endpoint is visible, which is right for calls the guest depends on
 * and wrong for this one. A tent with bad signal would file a row per arrival
 * and bury the failures that matter. Nothing here is awaited by anything, and
 * every outcome is ignored.
 *
 * ⚠️ ONCE PER PAGE LOAD, ENFORCED BY A MODULE-LEVEL FLAG. React double-invokes
 * effects in StrictMode during development, and this page re-renders roughly
 * once a second while a portrait paints. Neither should count as an arrival.
 * The server's unique index is the real guard (sql/043) — this just avoids
 * making it work for a living.
 *
 * ⚠️ IT MUST NOT BLOCK ANYTHING. `getBoothToken()` is awaited only inside the
 * detached async, so a slow or failing session mint delays a log row and
 * nothing else. The guest never waits on this and never sees it fail.
 */
import { getBoothToken } from './boothSession';
import { getPlacement } from './placement';

let sent = false;

/* Rough, and rough is fine. It separates "walked up with a phone" from "opened
   it on a laptop", which is all `source` is ever asked. It is not a
   capability check and nothing branches on it. */
function guessSource() {
  try {
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ? 'mobile' : 'web';
  } catch {
    return 'web';
  }
}

export function reportVisit() {
  if (sent) return;
  sent = true;

  (async () => {
    try {
      const token = await getBoothToken();
      /* No token means the session mint failed, and that guest has bigger
         problems than attribution. Drop it. */
      if (!token) return;

      await fetch('/api/booth-visit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
        /* ⚠️ `keepalive` so the arrival still goes out if the guest taps
           straight through to the next screen. Without it a fast tapper is an
           uncounted impression, and fast tappers are exactly the people who
           convert. */
        keepalive: true,
        body: JSON.stringify({
          placement: getPlacement(),
          source: guessSource(),
        }),
      });
    } catch {
      /* Deliberately silent. An impression is worth nothing next to a
         portrait, and this must never surface to a guest. */
    }
  })();
}
